import { all } from 'async-saga';
import dayjs from 'dayjs';
import { actions } from '..';
import * as api from '../../api';
import * as callApi from '../../api/call-control';
import * as contactApi from '../../api/contact-control';
import * as queueApi from '../../api/queue-control';
import { getContactType, getContactTypeString } from '../../Components/AgentView/ContactPage';
import { AlertIconType, showAlertDialog, showConfirmWindow } from '../../legacy';
import { CallState, canQueueResume, canRetrieveCall, ClidObjectType, DispositionCodeAction, EMPTY_GUID, EntityType, ExtensionStatus, sameIdentifier, SERVER_DATE_FOMATE } from '../../model';
import { setWindowOnMedia } from '../actions';
import { selectMyDevices, selectMyself } from '../reducers/config';
import { selectEmail, selectEmailSending, selectReplyEmail, Sending } from '../reducers/emails';
import { selectDirectoryView } from '../reducers/localSettings';
import { selectMedia, selectMediaNotes } from '../reducers/media';
import { getOutBoundById } from '../reducers/outbound';
import { selectExtension } from '../reducers/queueAgents';
import { takeEvery, waitForState } from '../tools/async-saga-types';

const saveMediaNotes = takeEvery(actions.saveMediaNotes, async (ctx, { id }) => {
  const myself = selectMyself(ctx.getState())
  if (myself) {
    const notes = selectMediaNotes(ctx.getState(), id)
    api.setMediaNotes(myself, id, notes)
  }
})

const featchMediaClidRequest = takeEvery(actions.featchMediaClidRequest, async function (ctx, { mediaIds }) {
  const myself = selectMyself(ctx.getState())

  if (myself) {
    for (let index = 0; index < mediaIds.length; index++) {
      const mediaId = mediaIds[index];
      const clid = await api.getMediaClid(myself, mediaId)

      await ctx.dispatch(actions.fetchMediaClidResponse(mediaId, clid))
    }
  }
})

const requestDeleteContact = takeEvery(actions.requestDeleteContact, async function (ctx, { mediaId, contact, description }) {
  const myself = selectMyself(ctx.getState())
  if (myself) {
    showConfirmWindow('Delete Contact', 'Are you sure you want to delete \'' + description + '\'?', async (action: string) => {
      if (action === "yes") {
        if (contact.id !== "0") {
          await contactApi.deleteContact(myself, contact)
          //delete store
          ctx.dispatch(actions.deleteContact(mediaId, contact))

          //hack contact out of the broadcast as datastore doesn't do it for us right now
          ctx.dispatch(actions.updateMediaClidObject(mediaId, '', ClidObjectType.none, ''))
          ctx.dispatch(actions.fetchMediaTasksRequest(mediaId, ''))
          ctx.dispatch(actions.showClientNotification("Contact has been deleted"))
        }
        else {
          ctx.dispatch(actions.showClientNotification("Contact details have been removed"))
        }
      }
    })
  }
})

const updateContact = takeEvery(actions.updateContact, async function (ctx, { mediaId, contact, reload }) {
  const myself = selectMyself(ctx.getState())
  const media = selectMedia(ctx.getState(), mediaId)
  //const transcription = getTranscriptions(ctx.getState(), mediaId)
  const view = selectDirectoryView(ctx.getState())

  if (myself) {
    if (contact.Description === '') {
      ctx.dispatch(actions.showClientNotification('You must enter a contact description.'))
      ctx.dispatch(actions.setSelectedContactState('none'))
      return
    }

    ctx.dispatch(actions.setSelectedContactState('saving'))
    const id = await contactApi.saveContact(myself, contact)

    if (media) {
      //lets see if there is a match
      const match = media.clid.toLowerCase() === contact.Business.toLowerCase() ||
        media.clid.toLowerCase() === contact.Home.toLowerCase() ||
        media.clid.toLowerCase() === contact.Mobile.toLowerCase() ||
        media.clid.toLowerCase() === contact.Email.toLowerCase()

      //hack contact into the broadcast as datastore doesn't do it for us right now
      if (match && media.clidObjectKey !== contact.ContactId.toString()) {
        ctx.dispatch(actions.updateMediaClidObject(mediaId, id.toString(), contact.ExtensionNumber === '0' ? ClidObjectType.companyContact : ClidObjectType.personalContact, contact.Description))
        ctx.dispatch(actions.fetchMediaTasksRequest(mediaId, id.toString()))
      } else if (!match && media.clidObjectKey === contact.ContactId.toString()) {
        ctx.dispatch(actions.updateMediaClidObject(mediaId, '', ClidObjectType.none, ''))
        ctx.dispatch(actions.fetchMediaTasksRequest(mediaId, ''))
      } else if (reload) {
        ctx.dispatch(actions.fetchMediaTaskItemsRequest(mediaId, EMPTY_GUID))
      }
    }

    /*
    if (transcription) {
      const call = transcription.calls.find(c => c.telephoneNumber === contact.Business.toLowerCase() ||
        c.telephoneNumber === contact.Home.toLowerCase() ||
        c.telephoneNumber === contact.Mobile.toLowerCase())
      if (call) {
        ctx.dispatch(actions.updateCallTranscriptionCall(mediaId, {
          ...call,
          displayName: contact.Description,
          contactId: contact.ContactId,
        }))
      }
    }
    */

    ctx.dispatch(actions.setSelectedContact({ id: id.toString(), server: contact.ServerName }))

    ctx.dispatch(actions.showClientNotification('Contact has been saved'))

    //used within the directory view
    ctx.dispatch(actions.setSelectedContactState('saved'))

    //if not on agent view
    if (view.entityType === EntityType.Contact && window.location.pathname.indexOf('/agent') === -1 && window.location.pathname.indexOf('/search')) {
      const type = getContactType(contact.ExtensionNumber)
      if (view.contactType !== type) {
        ctx.dispatch(actions.showClientNotification('To view \'' + contact.Description + '\' you need to change the view to ' + getContactTypeString(contact.ExtensionNumber)))
        ctx.dispatch(actions.setSelectedContact(undefined))
      }
    }

  }
})

const requestMediaTakeItem = takeEvery(actions.requestMediaTakeItem, async function (ctx, { itemId, mediaType }) {
  const myself = selectMyself(ctx.getState())

  if (myself) {
    const x = await queueApi.takeMediaItem(itemId, mediaType)

    if (x.resultCode !== 0) {
      showAlertDialog("Error", x.resultDescription, AlertIconType.Error)
    } else {
      ctx.dispatch(actions.openAgentView(x.datastoreItemId))
    }
  }
})

const completeMedia = takeEvery(actions.completeMedia, async function (ctx, { mediaId }) {
  const state = ctx.getState()
  const myself = selectMyself(state)
  const devices = selectMyDevices(state)

  if (!myself || !devices) { return }

  const media = selectMedia(state, mediaId)
  if (!media) { return }

  if (media.notes !== undefined) {
    await api.setMediaNotes(myself, mediaId, media.notes)
  }
  
  let forceWrapup = media.queueInfo && media.queueInfo.forcedWrapup

  //might not have it so get it (on a refresh)
  if (media.queueInfo === undefined) {
    if (media.isQueueCall) {
      const queue = await queueApi.getQueueDetails(media.queueId)
      ctx.dispatch(actions.updateMediaQueueInfo(media.id, queue.forcedWrapup))

      forceWrapup = queue.forcedWrapup
    } else {
      forceWrapup = false
    }
  }

  if (media.mediaType === 'OUT') {

    //out bound needs a wrapup before code is set otherwise it is gone
    if (media.wrapupCodes.length === 0 && forceWrapup) {
      ctx.dispatch(actions.openAgentView(mediaId, 'wrapups'))
      ctx.dispatch(actions.showClientNotification('Please select wrapup codes first'))
      return
    }

    const outbound = getOutBoundById(state, media.mediaItemId)
    if (outbound) {
      if (!outbound.selectedCode) {
        ctx.dispatch(actions.showClientNotification('Please select a Disposition Code first'))
        return
      }

      //send an wait for response
      await queueApi.outboundSetCode(mediaId, outbound.contactId, outbound.selectedCode.iD, outbound.selectedCode.action, outbound.selectedCode.action === DispositionCodeAction.Defer && outbound.deferDate ? dayjs(outbound.deferDate).utc().format(SERVER_DATE_FOMATE) : '')

    }
  }
  else if (media.mediaType === 'EMAIL') {
    const email = selectEmail(state, mediaId)
    const reply = selectReplyEmail(state, mediaId)

    if (email) {
      if (reply && selectEmailSending(state, mediaId) === Sending.Writing) {
        ctx.dispatch(actions.showClientNotification('Please send or cancel your email'))
        return
      }
      await waitForState(ctx, s => selectEmailSending(s, mediaId) !== Sending.Sending)
    }
  }

  // if wrapup is forced and the user hasn't selected anything yet - take them to the wrapups tab and tell them
  if (media.wrapupCodes.length === 0 && forceWrapup) {
    ctx.dispatch(actions.openAgentView(mediaId, 'wrapups'))
    ctx.dispatch(actions.showClientNotification('Please select wrapup codes first'))
    return
  }

  // send any selected wrapup codes
  if (media.wrapupCodes.length) {
    //this was done for call assistants
    if (media.deviceId !== media.extensionId) {
      await api.setWrapupCodes(myself, media.extensionId, mediaId, media.wrapupCodes)
    } else {
      await api.setWrapupCodes(myself, myself.id, mediaId, media.wrapupCodes)
    }
  }

  //don't close media if parked
  // if(media.mediaType !== 'CALL' && media.mediaType !== 'VM' && media.status === CallState.QueueHold){
  //   ctx.dispatch(actions.showClientNotification('Media cannot be completed while Parked'))
  // }

  if (media.mediaType !== 'CALL' && media.mediaType !== 'VM' && media.status !== CallState.WaitingForWrapup && media.status !== CallState.QueueHold && media.extensionId === myself.id) {
    await queueApi.closeMediaItem(mediaId)
  }

  //so if call has already terminiated then status will be none so we then remove the call
  if ((media.mediaType === 'CALL' || media.mediaType === 'VM') && media.status === CallState.None) {
    ctx.dispatch(actions.removeMedia(media.id))
  }

  //so if call has already terminiated then status will be none so we then remove the call
  if ((media.mediaType === 'TC') && media.status === CallState.None) {
    ctx.dispatch(actions.requestLeaveConversation(media.mediaItemId))
  }
})


// eslint-disable-next-line @typescript-eslint/no-unused-vars
function showConfirmation(title: string, html: string) {
  return new Promise(resolve => {
    showConfirmWindow(title, html, async (action: string) => {
      resolve(action === 'yes')
    })
  })
}

const transferMedia = takeEvery(actions.dropMedia, async (ctx, { id, to, toIndex }) => {
  const store = ctx.getState()
  const myId = selectMyself(store)
  const devices = selectMyDevices(store)
  const media = selectMedia(store, id)

  if (!devices || !media || !to.agent || !myId) {
    return
  }

  const server = to.server
  const extension = to.agent
  const toMyself = myId && sameIdentifier(myId, { server, id: extension })
  const agent = selectExtension(store, { server, id: extension })

  if (!agent || (agent && (media.mediaType === 'CALL' || media.mediaType === 'VM') && !(agent.status === ExtensionStatus.None || agent.status === ExtensionStatus.Released))) {
    return
  }

  const canRetrieve = canRetrieveCall([media], agent.status) || canQueueResume([media], agent.status)
  if (!canRetrieve) {
    return
  }

  // let ther server do it
  //ctx.dispatch(actions.transferMedia(id, to, toIndex))

  if (media.status === CallState.ExtensionPending || media.status === CallState.QueueHold || media.status === CallState.Queueing) {

    if (toMyself && media.mediaType !== 'VM' && media.mediaType !== 'CALL') {
      await ctx.dispatch(actions.ccSetForAutoAnswer(media.id))
    }

    //auto answer will only work for calls otherwise ccSetForAutoAnswer does it
    await callApi.queuePickup(extension, media.mediaType, id, to.queue, toMyself ? true : false);
  }
})

const onOpenWindowInUrl = takeEvery(actions.openWindowInUrl, async (ctx, { mediaId, url }) => {
  const store = ctx.getState()
  const media = selectMedia(store, mediaId)

  if (!media) {
    return
  }

  if (media.window === true) {
    console.info("Script uri lanuch [window already open] " + media.callTransactionId)
    return
  }

  await ctx.dispatch(setWindowOnMedia(mediaId))

  console.info("Script uri lanuch [" + url + "] " + media.callTransactionId)
  window.open(url, 'launch' + media.callTransactionId)
})

export default all(
  saveMediaNotes,
  featchMediaClidRequest,
  requestDeleteContact,
  updateContact,
  requestMediaTakeItem,
  completeMedia,
  transferMedia,
  onOpenWindowInUrl,
)
